import React, { useState } from "react";
import {
  Box,
  Container,
  Tab,
  Tabs,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import TrainingPlanHeader from "./TrainingPlanHeader";
import WorkoutOverview from "./WorkoutOverview";
import WorkoutDayView from "./WorkoutDayView";
import ExerciseDetails from "./ExerciseDetails";

/**
 * Main content component for training plan viewer
 * Provides tabbed navigation between overview, daily views, and exercise details
 */
const TrainingPlanContent = ({
  planData,
  exercisesMap,
  organizationName,
  brandingColors,
  organizationId,
  protocolId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [activeTab, setActiveTab] = useState(0);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedExercise, setSelectedExercise] = useState(null);

  // Helper functions
  const isRestDay = (split) => {
    if (!split || !split.name) return true;
    return (
      split.isRestSplit ||
      split.name.toLowerCase().includes("rest") ||
      split.name.toLowerCase().includes("off") ||
      split.name.toLowerCase().includes("recovery")
    );
  };

  const getExercise = (exerciseId) => {
    return (
      exercisesMap[exerciseId] || {
        id: exerciseId,
        name: `Exercise ID: ${exerciseId}`,
        description: "",
        instructions: "",
        videos: [],
        images: [],
        equipment: [],
      }
    );
  };

  const formatExerciseName = (exerciseId) => {
    if (!exerciseId) {
      return "Unknown Exercise";
    }
    
    const exercise = exercisesMap[exerciseId];
    if (exercise?.name) {
      return exercise.name;
    }
    
    // Fallback: format the exercise ID in a more readable way
    // Only show "Loading..." if we truly have no data at all
    if (Object.keys(exercisesMap).length === 0) {
      return "Loading...";
    }
    
    return exerciseId.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSelectedDay(null);
    setSelectedExercise(null);
  };

  const handleDaySelect = (day) => {
    setSelectedDay(day);
    setActiveTab(1); // Switch to day view tab when a day is selected
  };

  const handleExerciseSelect = (exerciseId) => {
    setSelectedExercise(exerciseId);
  };

  const handleBackToOverview = () => {
    setSelectedDay(null);
    setActiveTab(0);
  };

  const handleBackToDay = () => {
    setSelectedExercise(null);
  };

  // If a specific exercise is selected, show exercise details
  if (selectedExercise) {
    return (
      <Container maxWidth="lg">
        <ExerciseDetails
          exerciseId={selectedExercise}
          exercise={getExercise(selectedExercise)}
          onBack={handleBackToDay}
        />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <TrainingPlanHeader
        planData={planData}
        organizationName={organizationName}
        brandingColors={brandingColors}
        totalWorkouts={planData.splits?.length || 0}
      />

      {/* Navigation Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={selectedDay !== null ? activeTab : 0}
          onChange={handleTabChange}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons="auto"
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: 500,
            },
          }}
        >
          <Tab label="Training Overview" />
          {selectedDay !== null && (
            <Tab label={`Day ${selectedDay + 1} Details`} />
          )}
        </Tabs>
      </Box>

      {/* Content */}
      {activeTab === 0 && !selectedDay && (
        <WorkoutOverview
          planData={planData}
          onDaySelect={handleDaySelect}
          isRestDay={isRestDay}
          brandingColors={brandingColors}
        />
      )}

      {activeTab === 1 && selectedDay !== null && (
        <WorkoutDayView
          planData={planData}
          dayIndex={selectedDay}
          onExerciseSelect={handleExerciseSelect}
          onBackToOverview={handleBackToOverview}
          isRestDay={isRestDay}
          getExercise={getExercise}
          formatExerciseName={formatExerciseName}
          brandingColors={brandingColors}
        />
      )}
    </Container>
  );
};

export default TrainingPlanContent;
